// Banger app builds. The list lives in one plain-text file, /downloads/latest.txt (public/downloads/latest.txt), that
// both this page and /install.sh read, so a build shows up in both places at once and neither ever links to a file
// that isn't published. Format: `<platform> <format> <url> <sha256> <bytes>` per line, `#` comments.

import type { Platform } from "./play.ts";

export type BuildPlatform = "macos-arm64" | "macos-x86_64" | "linux-x86_64" | "windows-x86_64";
export type BuildFormat = "dmg" | "app.tar.gz" | "deb" | "exe";

export interface Build {
  platform: BuildPlatform;
  format: BuildFormat;
  url: string;
  sha256: string;
  bytes: number;
}

const PLATFORMS: BuildPlatform[] = ["macos-arm64", "macos-x86_64", "linux-x86_64", "windows-x86_64"];
const FORMATS: BuildFormat[] = ["dmg", "app.tar.gz", "deb", "exe"];
export const MANIFEST_PATH = "/downloads/latest.txt";

/** Lines that don't parse are skipped: a bad line never becomes a link. Only https URLs or paths on this site. */
export function parseManifest(text: string): Build[] {
  const out: Build[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    const [platform, format, url, sha256, bytes, ...rest] = line.split(/\s+/);
    if (rest.length) continue;
    if (!PLATFORMS.includes(platform as BuildPlatform) || !FORMATS.includes(format as BuildFormat)) continue;
    if (!url || !(/^https:\/\/[^\s/]+\/\S+$/.test(url) || /^\/downloads\/[A-Za-z0-9._+-]+$/.test(url))) continue;
    if (!sha256 || !/^[0-9a-f]{64}$/.test(sha256)) continue;
    const n = Number(bytes);
    if (!Number.isSafeInteger(n) || n <= 0) continue;
    out.push({ platform: platform as BuildPlatform, format: format as BuildFormat, url, sha256, bytes: n });
  }
  return out;
}

export async function loadBuilds(): Promise<Build[]> {
  try {
    const r = await fetch(MANIFEST_PATH, { cache: "no-cache" });
    return r.ok ? parseManifest(await r.text()) : [];
  } catch {
    return [];
  }
}

/** What the page offers per computer: the label people know, its requirements, and the builds for it. */
export interface Offer {
  platform: Exclude<Platform, "mobile" | "other">;
  label: string;
  requirements: string;
  builds: Build[];
}

export function offers(builds: Build[]): Offer[] {
  const of = (prefix: string) => builds.filter((b) => b.platform.startsWith(prefix));
  return [
    { platform: "mac", label: "macOS", requirements: "macOS 13 or later, Apple silicon first", builds: of("macos-") },
    { platform: "linux", label: "Linux", requirements: "Ubuntu 24.04 or later (.deb), x86-64", builds: of("linux-") },
    { platform: "windows", label: "Windows", requirements: "Windows 10 or later", builds: of("windows-") },
  ];
}

export function fileName(url: string): string {
  return decodeURIComponent(url.slice(url.lastIndexOf("/") + 1));
}

export function formatBytes(n: number): string {
  if (n >= 1024 * 1024 * 1024) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 * 1024) return `${Math.round(n / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

export function archLabel(p: BuildPlatform): string {
  return { "macos-arm64": "Apple silicon", "macos-x86_64": "Intel", "linux-x86_64": "x86-64", "windows-x86_64": "x86-64" }[p];
}
